import { PartialNullable } from "../../Extensions";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { SvgHelper } from "../SvgHelper";
import { Accordion } from "./Accordion";
import { ContainerControl, ContainerControlPropertyValues, IContainerControl } from "./ContainerControl";
import { NAMESPACE } from "./_Namespace";

import "./AccordionPanel.scss";

export type AccordionPanelPropertyValues =
    ContainerControlPropertyValues
    & {
        dropdownSvgKeyPrefix: string;
        icon: string;
        svgKey: string;
    };

export type AccordionPanelProperties = {
    [name in keyof AccordionPanelPropertyValues]: Property<AccordionPanelPropertyValues[name]>
};

export interface IAccordionPanel extends IContainerControl, AccordionPanelProperties
{
}

export class AccordionPanel extends ContainerControl implements IAccordionPanel
{
    // #region Static

    public static override classNamespace = NAMESPACE + "AccordionPanel";
    public static override className = "AccordionPanel";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<AccordionPanelPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                text: "Panel",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public _containerWrapperElementRef!: ElementRef;
    protected _dropdownIconWrapperElementRef!: ElementRef;
    protected _dropdownSvgWrapperElementRef!: ElementRef;
    protected _iconElementRef!: ElementRef;
    protected _iconWrapperElementRef!: ElementRef;
    protected _svgWrapperElementRef!: ElementRef;
    public _titleElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public dropdownSvgKeyPrefix!: PropertyString;
    public icon!: PropertyString;
    public svgKey!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<AccordionPanelPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-accordion-panel");
        this._containerWrapperElementRef.current.addClass("rad-control-container-wrapper content");

        // Trigger Property Changes
        this.dropdownSvgKeyPrefix.triggerChange();
        this.icon.triggerChange();
        this.svgKey.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public close(immediately: boolean)
    {
        const
            accordion = this.parentControlGet() as Accordion;

        if (!accordion)
            return;

        accordion.close(this.indexGet(), immediately);
    }
    public indexGet()
    {
        const
            accordion = this.parentControlGet() as Accordion;

        if (!accordion)
            return -1;

        const
            accordionPanels = accordion.controlsGet();

        for (let i = 0; i < accordionPanels.length; i++)
        {
            if (accordionPanels[i] === this)
                return i;
        }

        return -1;
    }
    public isOpen()
    {
        return this.$$element.get()?.find(">.title").hasClass("active") ?? false;
    }
    public open()
    {
        const
            accordion = this.parentControlGet() as Accordion;

        if (!accordion)
            return;

        accordion.open(this.indexGet());
    }
    public removeActiveClass()
    {
        this.$$element.get()?.removeClass("active");
        this._triggerAncestorVisibilityChanged(new PropertyChange<boolean>(false, true));
    }

    // #endregion

    // #region Protected Methods

    protected _dropdownSvgKeyPrefixChanged(change: PropertyChange<string>)
    {
        this._dropdownSvgWrapperElementRef.current.empty();
        this._dropdownSvgWrapperElementRef.current.append(SvgHelper.createSvg(`${change.currentValue}expand`).addClass("rad-action-expand"));
        this._dropdownSvgWrapperElementRef.current.append(SvgHelper.createSvg(`${change.currentValue}collapse`).addClass("rad-action-collapse"));
        this._updateLayout();
    }
    protected _iconChanged(change: PropertyChange<string>)
    {
        if (change.originalValue)
            this._iconElementRef.current.removeClass(change.originalValue);

        if (change.currentValue)
        {
            this.addCssClass("rad-control-accordion-panel-icon");
            this._iconElementRef.current.addClass(change.currentValue);
        }
        else
            this.removeCssClass("rad-control-accordion-panel-icon");

        this._updateLayout();
    }
    protected _svgKeyChanged(change: PropertyChange<string>)
    {
        this._svgWrapperElementRef.current.empty();

        if (change.currentValue)
            this._svgWrapperElementRef.current.append(SvgHelper.createSvg(change.currentValue));

        this._updateLayout();
    }
    public _updateLayout()
    {
        const
            icon = this.icon.get(),
            svgKey = this.svgKey.get();

        if (icon)
        {
            this._iconWrapperElementRef.current.show();
            this._svgWrapperElementRef.current.hide();
        }
        else
        {
            this._iconWrapperElementRef.current.hide();

            if (svgKey)
                this._svgWrapperElementRef.current.show();
            else
                this._svgWrapperElementRef.current.hide();
        }

        const
            dropdownSvgKeyPrefix = this.dropdownSvgKeyPrefix.get();

        if (!dropdownSvgKeyPrefix)
        {
            this._dropdownIconWrapperElementRef.current.show();
            this._dropdownSvgWrapperElementRef.current.hide();
        }
        else
        {
            this._dropdownIconWrapperElementRef.current.hide();
            this._dropdownSvgWrapperElementRef.current.show();
        }

        const
            isOpen = this.isOpen();

        if (isOpen)
            this.addCssClass("rad-state-open");
        else
            this.removeCssClass("rad-state-open");
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: AccordionPanelPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._containerWrapperElementRef = new ElementRef();
        this._dropdownIconWrapperElementRef = new ElementRef();
        this._dropdownSvgWrapperElementRef = new ElementRef();
        this._iconElementRef = new ElementRef();
        this._iconWrapperElementRef = new ElementRef();
        this._svgWrapperElementRef = new ElementRef();
        this._titleElementRef = new ElementRef();

        this.dropdownSvgKeyPrefix = new PropertyString(this, propertyValues.dropdownSvgKeyPrefix, this._dropdownSvgKeyPrefixChanged);
        this.icon = new PropertyString(this, propertyValues.icon, this._iconChanged);
        this.svgKey = new PropertyString(this, propertyValues.svgKey, this._svgKeyChanged);
    }

    protected override _createElementComplete()
    {
        super._createElementComplete();

        this._svgWrapperElementRef.current.hide();
        this._iconWrapperElementRef.current.hide();
        this._dropdownIconWrapperElementRef.current.hide();
        this._dropdownSvgWrapperElementRef.current.hide();
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div class="rad-control rad-control-container-control rad-control-accordion-panel">
                <div ref={this._titleElementRef} class="title">
                    <span ref={this._svgWrapperElementRef} class="rad-svg-wrapper">
                    </span>
                    <span ref={this._iconWrapperElementRef} class="rad-icon-wrapper">
                        <i ref={this._iconElementRef} class="icon">
                            <span></span>
                        </i>
                    </span>
                    <span class="text"></span>
                    <i ref={this._dropdownIconWrapperElementRef} class="rad-dropdown-icon-wrapper dropdown icon"></i>
                    <span ref={this._dropdownSvgWrapperElementRef} class="rad-dropdown-svg-wrapper"></span>
                </div>
                <div ref={this._containerWrapperElementRef} class="rad-control-container-wrapper content">
                    <div ref={this._containerElementRef} class="rad-control-container">
                        {children ?? this._createChildrenHtml()}
                    </div>
                </div>
            </div>
        );
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);
        this._titleElementRef.current.find(">span.text").text(change.currentValue);
    }

    // #endregion

    // #endregion
}